import {
  GlobalKeysContext,
  type GlobalKeysContextState,
  type KeyBinding
} from '@bonsai-components/react-global-keys';
import React, { Component, type JSX, type ReactNode } from 'react';
import { CommandPalette } from '../components/command-palette/command-palette.component';
import { globalSearchEvent } from '../hooks/use-app-event.hooks';
import { excludeActionFromInputMode } from '../hooks/use-command-palette.hooks';

type CommandWithKeyBindingWithKey = {
  key: string;
  modifier?: KeyBinding['modifier'];
  includeInputMode?: boolean;
};

type CommandWithKeyBindingWithCode = {
  code: string;
  modifier?: KeyBinding['modifier'];
  includeInputMode?: boolean;
};

export type CommandWithKeyBinding =
  | CommandWithKeyBindingWithKey
  | CommandWithKeyBindingWithCode;

export const isCommandWithKeyBindingWithKey = (
  command: CommandWithKeyBinding
): command is CommandWithKeyBindingWithKey => {
  return (command as CommandWithKeyBindingWithKey).key !== undefined;
};

type CommandPaletteContextProps = {
  children: ReactNode[] | ReactNode;
};

export type CommandPaletteItem = {
  label: string;
  description?: string;
  keyboardShortcuts?: CommandWithKeyBinding | CommandWithKeyBinding[];
  adminOnly?: boolean;
  group?: 'Actions' | 'Navigation' | 'Admin only';
  action: (args?) => void;
};

type CommandPaletteContextState = {
  commands: CommandPaletteItem[];
  showPalette: boolean;
  togglePalette: () => void;
  addCommands: (commands: CommandPaletteItem | CommandPaletteItem[]) => void;
  removeCommands: (commands: string | string[]) => void;
};

const initialState: CommandPaletteContextState = {
  commands: [],
  showPalette: false,
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  togglePalette: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  addCommands: (_commands: CommandPaletteItem | CommandPaletteItem[]) => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  removeCommands: (_commands: string | string[]) => {}
};

export const CommandPaletteContext = React.createContext(initialState);

export class CommandPaletteProvider extends Component<
  CommandPaletteContextProps,
  CommandPaletteContextState
> {
  static contextType = GlobalKeysContext;
  context: GlobalKeysContextState;
  readonly state: CommandPaletteContextState = {
    ...initialState,
    togglePalette: () => {
      this.setState(() => ({
        showPalette: !this.state.showPalette
      }));
    },
    addCommands: (commands) => {
      const newCommands = Array.isArray(commands) ? commands : [commands];

      this.setState((prevState) => ({
        commands: [
          ...prevState.commands,
          ...newCommands.filter(
            (newCmd) =>
              !prevState.commands.some((cmd) => cmd.label === newCmd.label)
          )
        ].sort((a, b) => a.label.localeCompare(b.label))
      }));
    },
    removeCommands: (commands) => {
      const newCommands = Array.isArray(commands) ? commands : [commands];

      this.setState((prevState) => ({
        commands: prevState.commands.filter(
          (cmd) => !newCommands.some((newCmd) => newCmd === cmd.label)
        )
      }));
    }
  };

  /**
   * On mount set the global key bindings
   */
  componentDidMount(): void {
    // Add "Open command palette" command
    this.state.addCommands({
      label: 'Open command palette',
      keyboardShortcuts: [
        {
          key: '?',
          modifier: {
            shift: true
          }
        }
      ],
      group: 'Actions',
      action: () => {
        this.state.togglePalette();
      }
    });

    // Add "Open search" command with fixed keybindings
    this.state.addCommands({
      label: 'Open search',
      keyboardShortcuts: [
        {
          key: 'k',
          modifier: { meta: true }
        }
      ],
      group: 'Actions',
      action: () => {
        globalSearchEvent.dispatch();
      }
    });

    this.context.addKeyBinding({
      key: '?',
      modifier: {
        shift: true
      },
      action: (e) => {
        excludeActionFromInputMode(e, (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.state.togglePalette();
        });
      }
    });

    this.context.addKeyBinding({
      key: 'k',
      modifier: { meta: true },
      action: (e) => {
        e.preventDefault();
        e.stopPropagation();
        globalSearchEvent.dispatch();
      }
    });
  }

  render(): JSX.Element {
    return (
      <CommandPaletteContext.Provider value={this.state}>
        {this.props.children}
        <CommandPalette />
      </CommandPaletteContext.Provider>
    );
  }
}
